import { useQuery, useReactiveVar } from "@apollo/client";
import { LocationGenerics } from "router/location";
import { dispatchModal, wrapClick } from "utils";
import { Modal, Tabs } from "components/layouts";
import {useState, useMemo} from "react";
import {  useSearch } from "react-location";
import { GET_ISSUE, GET_TICKET_DETAILS } from "./apollo";
import { currentUserVar } from "apollo/cache/auth";
import { IssueResolutionView, IssueDetailsView, IssueHistoryView} from "components/cards";
import {Loader} from "components";
import { ClickupTaskView } from "components/cards";



export default function ViewTicketContainer({
  open,
  setOpen
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const currentUser = useReactiveVar(currentUserVar);
  const [issueTab, setIssueTab] = useState("IssueDetails");

  const { data, loading } = useQuery(GET_ISSUE, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });


  const issueTabs = useMemo(() => [
    {
      name: "Issue Details",
      href: "IssueDetails",
      activeStatuses: [
        "ReportedToFocalPerson",
        "ReferredToPowerUser",
        "ReassignedToPowerUser",
        "EscalatedToPolymorphLabs",
        "Resolved",
        "Rejected",
        "Reworking",
        "Completed",
        "Reassigned",
      ],
    },
    {
      name: "System Ticket Details",
      href: "ClickupTask",
      activeStatuses: [
        "ReportedToFocalPerson",
        "ReferredToPowerUser",
        "ReassignedToPowerUser",
        "EscalatedToPolymorphLabs",
        "Resolved",
        "Rejected",
        "Reworking",
        "Completed",
        "Reassigned",
      ],
      isActive: () => Boolean(data?.issue?.clickupTicketId)
    },
    {
      name: "Issue History",
      href: "IssueHistory",
      activeStatuses: [
        "ReportedToFocalPerson",
        "ReferredToPowerUser",
        "ReassignedToPowerUser",
        "EscalatedToPolymorphLabs",
        "Resolved",
        "Rejected",
        "Reworking",
        "Completed",
        "Reassigned",
      ],
    },
    {
      name: "Resolution Details",
      href: "ResolutionDetails",
      activeStatuses: ["Resolved", "Completed"],
    },
  ], [data]);


  const getActionType = () => {
    const currentStage = data?.issue?.currentStage?.stage;
    const isFocalPerson = currentUser?.isFocalPerson;
    const isPowerUser = currentUser?.isPowerUser;

    if (currentStage === "Reassigned") {
      if (isFocalPerson && isPowerUser) return "escalate";
      if (isFocalPerson) return "refer-power";
      if (isPowerUser) return "escalate";
    }

    return currentStage === "ReportedToFocalPerson"
      ? "refer-power"
      : "escalate";
  };

  const getButtonLabel = () => {
    const currentStage = data?.issue?.currentStage?.stage;
    const isFocalPerson = currentUser?.isFocalPerson;
    const isPowerUser = currentUser?.isPowerUser;

    if (currentStage === "Reassigned") {
      if (isFocalPerson && isPowerUser) return "Escalate to Polymorph";
      if (isFocalPerson) return "Refer To Power User";
      if (isPowerUser) return "Escalate to Polymorph";
    }

    return currentStage === "ReportedToFocalPerson"
      ? "Refer To Power User"
      : "Escalate to Polymorph";
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      hidePadding
      title="Issue Information"
      size="4xl"
      description="Details of the issue is shown below"
      renderActions={() => (
        <>
          {data?.issue?.currentStage?.escalatedTo?._id === currentUser?._id &&
            [
              "ReportedToFocalPerson",
              "ReferredToPowerUser",
              "ReassignedToPowerUser",
              "Reassigned",
            ].includes(data?.issue?.currentStage.stage) && (
              <>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={wrapClick(
                    dispatchModal(data?.issue?._id, getActionType())
                  )}
                >
                  {getButtonLabel()}
                </button>

                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={wrapClick(
                    dispatchModal(data?.issue?._id, "resolve")
                  )}
                >
                  Resolve Issue
                </button>
              </>
            )}

          {data?.issue?.currentStage?.escalatedTo?._id === currentUser?._id &&
            [
              "ReferredToPowerUser",
              "ReassignedToPowerUser",
            ].includes(data?.issue?.currentStage.stage) && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(dispatchModal(data?.issue?._id, "reassign"))}
              >
                Reassign
              </button>
            )}

          {data?.issue?.createdBy === currentUser?._id &&
            ["Resolved", "EscalatedToPolymorphLabs"].includes(
              data?.issue?.currentStage?.stage
            ) && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(dispatchModal(data?.issue?._id, "reject"))}
              >
                Reject Issue Resolution
              </button>
            )}

          {data?.issue?.createdBy === currentUser?._id &&
            ["Resolved", "EscalatedToPolymorphLabs"].includes(
              data?.issue?.currentStage?.stage
            ) && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(dispatchModal(data?.issue?._id, "approve"))}
              >
                Approve Issue Resolution
              </button>
            )}

          {data?.issue?.createdBy === currentUser?._id && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(dispatchModal(data?.issue?._id, "update"))}
            >
              Edit
            </button>
          )}
        </>
      )}
    >
      {data?.issue?._id && (
        <>
          {[
            "ReportedToFocalPerson",
            "ReferredToPowerUser",
            "ReassignedToPowerUser",
            "EscalatedToPolymorphLabs",
            "Resolved",
            "Rejected",
            "Reworking",
            "Completed",
            "Reassigned",
          ].includes(data?.issue?.currentStage?.stage) && (
            <Tabs
              tabs={issueTabs}
              onTabChanged={setIssueTab}
              status={data?.issue?.currentStage?.stage}
            />
          )}
          <div className="flex-1 w-full min-h-[65vh] max-h-[65vh] overflow-y-auto ">
            {issueTab === "IssueDetails" && (
              <IssueDetailsView issue={data?.issue} />
            )}
            {issueTab === "ClickupTask" && (
              <ViewClickupTaskContainer issue={data?.issue} />
            )}
            {issueTab === "IssueHistory" && (
              <IssueHistoryView history={data?.issue?.stages} />
            )}
            {issueTab === "ResolutionDetails" && (
              <IssueResolutionView issue={data?.issue} />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}


function ViewClickupTaskContainer({ issue }: { issue?: any }){

  const { data, loading } = useQuery(GET_TICKET_DETAILS, {
    variables: {
      ticketId: issue?.clickupTicketId,
    },
    skip: !Boolean(issue?.clickupTicketId),
  });

  if(loading){
    return (
      <div className={"w-full h-full pt-16"}>
        <Loader />
      </div>
    )
  }

  return (
    <ClickupTaskView task={data?.clickupTask} />
  )

}